'use client';

import { useEffect, useMemo, useState } from 'react';

import {
  buildPinEntries,
  countPinsByDevice,
  groupPinsByPage,
  resolveDeviceFilter,
} from './sidebarPins';

import type { DeviceFilter, SortOrder } from './types';
import type { DevicePreset, Pin } from '@/types/feedback';

interface UseSidebarPinsParams {
  pins: Pin[];
  device: DevicePreset;
  allDevices: boolean;
  sortOrder: SortOrder;
}

export function useSidebarPins({
  pins,
  device,
  allDevices,
  sortOrder,
}: UseSidebarPinsParams) {
  const [deviceFilter, setDeviceFilter] = useState<DeviceFilter>(() =>
    resolveDeviceFilter(allDevices, device),
  );

  // Re-sync the filter when the workbench view/device changes (e.g. toggling to
  // canvas flips allDevices), otherwise a stale filter hides pins.
  useEffect(() => {
    setDeviceFilter(resolveDeviceFilter(allDevices, device));
  }, [allDevices, device]);

  const filterCounts = useMemo(() => countPinsByDevice(pins), [pins]);

  const visiblePins = useMemo(
    () => buildPinEntries(pins, deviceFilter, sortOrder),
    [pins, deviceFilter, sortOrder],
  );

  const pinGroups = useMemo(() => groupPinsByPage(visiblePins), [visiblePins]);

  // The heading always reflects every open comment, not just the active tab.
  const openCommentCount = filterCounts.all;

  return {
    deviceFilter,
    setDeviceFilter,
    filterCounts,
    visiblePinCount: visiblePins.length,
    openCommentCount,
    pinGroups,
  };
}
